/**
 * Shared "EazyBooking vs <Competitor>" comparison page.
 *
 * Born 2026-05-29 when we audited the existing /calendly-alternative
 * page (442 LOC, hand-written) for F-500 2026 polish before shipping
 * three more competitor pages (Setmore / Acuity / Cal.com). The honest
 * verdict was: structure is solid, but copy-pasting 4× would lock in
 * the same gaps four times over. Extracted as a single configurable
 * component so:
 *
 *   1. Every comparison page automatically inherits F-500 polish
 *      (Schema.org + Breadcrumb + FAQ-section + honest verdict block +
 *      OG-image-friendly metadata helpers + Reveal animations).
 *   2. Adding a fifth competitor later costs ~1h not 6h — only the
 *      data file changes.
 *   3. Improvements made once propagate everywhere (e.g. a future
 *      hreflang/i18n lift only needs to happen in this file).
 *
 * Anti-FUD policy preserved from the original Calendly page: every
 * comparison row must be factually defensible. We don't claim things
 * the competitor does that we don't, and we honestly note where the
 * competitor is stronger ("Calendly is better when …").
 */
'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  ArrowRight,
  Award,
  Calendar,
  Check,
  Code2,
  Globe,
  Minus,
  Shield,
  Sparkles,
  X,
} from 'lucide-react';
import Navbar from '@/components/landing/navbar/Navbar';
import FooterV3 from '@/components/landing/footer/FooterV3';
import {
  CTAButton,
  Reveal,
  Section,
  SectionHeader,
} from '@/components/landing/_shared';
import BreadcrumbJsonLd from '@/components/seo/BreadcrumbJsonLd';
import { trackLandingCtaClicked } from '@/lib/analytics/posthog';
import {
  type Currency,
  DEFAULT_CURRENCY,
  getMonthlyPrice,
} from '@/lib/pricing/currency';

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || 'https://eazybooking.de';

/**
 * Normalized-competitor-name → our own comparison-page route slug.
 *
 * SEO rationale (Income Stream Surfers + Breaking B2B, both name internal
 * links from indexed pages as the #1 ranking lever): the listicle on each
 * page names other vendors — when we *have* a comparison page for that
 * vendor, we link to it. This weaves all 12 comparison pages into a dense
 * internal-link mesh automatically, so each indexed page passes signal to
 * the others. Key = name.toLowerCase() stripped to [a-z0-9].
 */
const COMPETITOR_SLUGS: Record<string, string> = {
  calendly: 'calendly-alternative',
  setmore: 'setmore-alternative',
  acuity: 'acuity-alternative',
  acuityscheduling: 'acuity-alternative',
  calcom: 'cal-com-alternative',
  treatwell: 'treatwell-alternative',
  fresha: 'fresha-alternative',
  planity: 'planity-alternative',
  phorest: 'phorest-alternative',
  belbo: 'belbo-alternative',
  studiolution: 'studiolution-alternative',
  shortcuts: 'shortcuts-alternative',
  ecut: 'e-cut-alternative',
};

/**
 * Industry landing-page deep-links. These 11 LPs live on a zero-authority
 * path and Google won't crawl-schedule them (verified via GSC: "URL unknown
 * to Google"). Naming them from the *indexed* comparison pages feeds crawl
 * demand into them — the one on-site lever left once homepage links alone
 * didn't move the needle. Canonical DE slugs to match the competitor-link
 * convention above; ns keys resolve to localized labels in comparisonUi. */
const INDUSTRY_LINKS: { ns: string; href: string }[] = [
  { ns: 'friseur', href: '/friseur-online-buchung' },
  { ns: 'nagel', href: '/nagelstudio-online-termine' },
  { ns: 'kosmetik', href: '/kosmetikstudio-terminbuchung' },
  { ns: 'massage', href: '/massage-terminbuchung' },
  { ns: 'werkstatt', href: '/werkstatt-termin-online-buchen' },
  { ns: 'reifenwechsel', href: '/reifenwechsel-termin-online' },
  { ns: 'tierarzt', href: '/tierarzt-online-termin' },
  { ns: 'heilpraktiker', href: '/heilpraktiker-online-termin' },
  { ns: 'coaching', href: '/coaching-online-termine' },
  { ns: 'physiotherapie', href: '/physiotherapie-online-termin' },
  { ns: 'yoga', href: '/yoga-studio-online-buchung' },
];

/** Resolves an alternative's display name to one of our comparison routes,
 *  or null when we have no page for it (then it stays plain text). */
function resolveCompetitorSlug(name: string): string | null {
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return COMPETITOR_SLUGS[key] ?? null;
}

/**
 * comparison-page slug → migration blog-guide slug.
 *
 * The winning DE rankers (Avana, studiolution) all dedicate a section to
 * "how to switch" — and we already have step-by-step migration guides on
 * the blog. This map surfaces a "switch in a few steps → guide" callout on
 * the comparison page when a guide exists, which also passes an internal
 * link to the (indexed) blog. Only the 5 competitors we have a guide for.
 */
const MIGRATION_GUIDES: Record<string, string> = {
  'fresha-alternative': 'fresha-verlassen',
  'treatwell-alternative': 'treatwell-verlassen',
  'calendly-alternative': 'calendly-wechsel',
  'planity-alternative': 'planity-zu-eazybooking-wechseln',
  'studiolution-alternative': 'studiolution-zu-eazybooking-wechseln',
};

/**
 * Initial number of comparison rows shown above the "Show all" toggle.
 * Tuned so the table fits one mobile viewport without scroll-fatigue —
 * F-500 pattern (Stripe pricing page, Linear features table).
 */
const TOP_ROWS = 5;

export type ComparisonValue = string | boolean;

export interface ComparisonRow {
  /** Feature name as it appears in the left-most column. */
  label: string;
  /** EazyBooking column — `true`/`false` render as green check / grey x;
   *  string renders as-is for nuanced "Inklusive ab Tag 1" answers. */
  eazybooking: ComparisonValue;
  /** Competitor column — same encoding as `eazybooking`. */
  competitor: ComparisonValue;
  /** Optional italic note appearing under the label — context, source,
   *  or honest caveat ("Calendly is better here because …"). */
  note?: string;
}

export interface QuickVerdictItem {
  /** Bullet text — short, one sentence. */
  text: string;
  /** Icon-tone: emerald = pro-EazyBooking, gray = neutral / pro-competitor. */
  tone: 'eazybooking' | 'competitor';
}

export interface DifferentiatorCard {
  /** Lucide icon name from the allow-list below ('globe' | 'shield' |
   *  'calendar' | 'code'). Constrained to keep the visual language
   *  cohesive across the four pages. */
  icon: 'globe' | 'shield' | 'calendar' | 'code';
  title: string;
  body: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * One entry in the "best <competitor> alternatives 2026" listicle.
 *
 * AEO/LLM rationale: a page that *only* compares EazyBooking vs the one
 * competitor is rarely cited by ChatGPT / Google AI-overviews — those
 * surface comprehensive "top N alternatives" listicles (Avana, Damin,
 * studiolution all rank this way). This section makes each comparison
 * page a genuine, citeable listicle: EazyBooking ranked #1 with the
 * honest "why", followed by a fair list of real other options.
 *
 * Anti-FUD policy applies — every competitor one-liner must be factually
 * defensible, not a strawman.
 */
export interface AlternativeEntry {
  /** Vendor name as shown in the ranked list heading. */
  name: string;
  /** `true` for the EazyBooking entry → rendered as the highlighted #1
   *  card with a recommendation badge + CTA. Exactly one entry should
   *  set this. */
  isUs?: boolean;
  /** One honest sentence: what this option is best at / who it fits. */
  pitch: string;
  /** Optional short "best for" tag rendered as a chip under the name. */
  bestFor?: string;
}

export interface ComparisonPageProps {
  /**
   * Competitor display name as it appears in copy ("Calendly", "Setmore", "Cal.com").
   */
  competitorName: string;
  /** Lower-case slug used in the URL — must match the route folder. */
  pageSlug: string;
  /** H1 + meta-title hero text. */
  heroTitle: string;
  /**
   * Hero subtitle paragraph(s). Pass as plain string or pre-rendered
   * ReactNode for multi-paragraph copy with `<strong>` highlights.
   */
  heroBody: ReactNode;
  /**
   * Honest "Verdict" panel — two columns. Bullets are sorted so the
   * pro-competitor side is on the left (gives away the bias signal
   * upfront, builds trust before the comparison table).
   */
  verdict: {
    competitorBetterWhen: QuickVerdictItem[];
    eazybookingBetterWhen: QuickVerdictItem[];
  };
  /**
   * Feature-by-feature rows. Order matters — put most-differentiating
   * rows at the top so users see the value-prop before scrolling
   * into the "we both have email reminders" tail.
   */
  comparison: ComparisonRow[];
  /** 4 differentiator cards with deeper context per killer feature. */
  differentiators: [DifferentiatorCard, DifferentiatorCard, DifferentiatorCard, DifferentiatorCard];
  /**
   * 4-6 FAQs that surface the rich-snippet on Google + handle the
   * remaining objections after the comparison table. Schema.org
   * FAQPage emitted automatically.
   */
  faqs: FaqItem[];
  /**
   * Optional "best <competitor> alternatives 2026" listicle. When present
   * (≥2 entries), renders the ranked listicle section — the AEO-critical
   * block that makes the page citeable by LLMs. Omit to skip the section.
   */
  alternatives?: AlternativeEntry[];
  /**
   * Geo-resolved currency for the SoftwareApplication Schema.org price
   * + any pricing copy. Pages call `resolveCurrencyFromRequest()` in
   * their server-component layer and pass the result here. Defaults
   * to EUR for local/dev where the Vercel geo-header is absent.
   */
  currency?: Currency;
}

/**
 * Computes "wins · ties · misses" tally from a comparison-row array.
 *
 * - **win**  = EazyBooking has the feature (true / non-empty string)
 *              AND competitor doesn't (false), OR strings differ in a
 *              way that signals EazyBooking-advantage (✓ prefix).
 * - **tie**  = both sides have it (true/true) or both lack it (false/false).
 * - **miss** = competitor has it and EazyBooking doesn't (false/true).
 *
 * Pure function with no side effects — easy to unit-test, deterministic
 * for SSR rendering parity. Heuristic chosen so the visible block
 * shows defensible numbers without manual annotation.
 */
function computeTally(rows: ComparisonRow[]): { wins: number; ties: number; misses: number } {
  let wins = 0;
  let ties = 0;
  let misses = 0;
  const isPositive = (v: ComparisonValue): boolean => {
    if (v === true) return true;
    if (v === false) return false;
    // Strings: "✓ …" or specific value count as positive; mere "Nein" /
    // "false-ish" labels as negative. The pages already start strong
    // positive cells with "✓" so this heuristic is reliable for our
    // own data.
    const trimmed = v.trim().toLowerCase();
    if (trimmed.startsWith('✓')) return true;
    if (trimmed === 'nein' || trimmed === 'no' || trimmed === '—') return false;
    return true; // a substantive non-empty value counts as positive
  };
  for (const r of rows) {
    const e = isPositive(r.eazybooking);
    const c = isPositive(r.competitor);
    if (e && !c) wins++;
    else if (!e && c) misses++;
    else ties++;
  }
  return { wins, ties, misses };
}

const ICONS = {
  globe: Globe,
  shield: Shield,
  calendar: Calendar,
  code: Code2,
} as const;

function ComparisonCell({
  value,
  yesLabel,
  noLabel,
}: {
  value: ComparisonValue;
  yesLabel: string;
  noLabel: string;
}) {
  if (value === true) {
    return (
      <div className="flex items-center gap-1.5">
        <Check className="h-4 w-4 text-emerald-600" strokeWidth={2.5} />
        <span className="text-sm font-medium text-gray-900">{yesLabel}</span>
      </div>
    );
  }
  if (value === false) {
    return (
      <div className="flex items-center gap-1.5">
        <X className="h-4 w-4 text-rose-400" strokeWidth={2.5} />
        <span className="text-sm text-gray-500">{noLabel}</span>
      </div>
    );
  }
  return <span className="text-sm text-gray-700">{value}</span>;
}

function TallyStat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone: 'win' | 'tie' | 'miss';
}) {
  // Tones picked so the win/miss contrast doesn't look like a sports
  // score — emerald/rose at low saturation, neutral grey for ties.
  const styles = {
    win: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    tie: 'border-gray-200 bg-gray-50 text-gray-700',
    miss: 'border-rose-200 bg-rose-50 text-rose-700',
  } as const;
  const Icon = tone === 'win' ? Check : tone === 'miss' ? X : Minus;
  return (
    <div
      className={`flex flex-col items-center rounded-xl border ${styles[tone]} px-3 py-3 text-center sm:flex-row sm:gap-3 sm:py-3 sm:text-left`}
    >
      <Icon
        className="h-5 w-5 flex-shrink-0 opacity-80"
        strokeWidth={2.5}
        aria-hidden="true"
      />
      <div className="mt-1 sm:mt-0">
        <div className="text-2xl font-semibold tabular-nums leading-none">
          {value}
        </div>
        <div className="mt-1 text-[11px] font-medium uppercase tracking-wide opacity-80 sm:text-xs">
          {label}
        </div>
      </div>
    </div>
  );
}

function Differentiator({ icon, title, body }: DifferentiatorCard) {
  const Icon = ICONS[icon];
  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-6">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-gray-950">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
    </div>
  );
}

export default function ComparisonPage({
  competitorName,
  pageSlug,
  heroTitle,
  heroBody,
  verdict,
  comparison,
  differentiators,
  faqs,
  alternatives,
  currency = DEFAULT_CURRENCY,
}: ComparisonPageProps) {
  const t = useTranslations('comparisonUi');
  const heroId = `${pageSlug}-hero`;
  const tableId = `${pageSlug}-vergleich-heading`;
  const diffId = `${pageSlug}-diff-heading`;
  const faqId = `${pageSlug}-faq-heading`;
  const altId = `${pageSlug}-alternativen-heading`;
  const migrationGuide = MIGRATION_GUIDES[pageSlug] ?? null;
  const [showAll, setShowAll] = useState(false);
  const tally = computeTally(comparison);
  const visibleRows = showAll ? comparison : comparison.slice(0, TOP_ROWS);
  const hasMoreRows = comparison.length > TOP_ROWS;
  const monthlyPrice = getMonthlyPrice(currency);

  // Schema.org SoftwareApplication — rich-snippet eligibility for the
  // comparison-table block. Re-emitted per page so each /xxx-alternative
  // route can stand on its own in search results.
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${APP_URL}/${pageSlug}#product`,
    name: 'EazyBooking',
    description:
      'Buchungs-Plattform für Service-Businesses mit eigener Storefront, Apple/Google/Outlook-Sync, DSGVO-konform, Server in Deutschland.',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: String(monthlyPrice),
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
    },
    url: APP_URL,
  };

  // Schema.org FAQPage — Google renders accordion rich-snippets when
  // 4+ FAQ items are present. Each Question/Answer pair must match the
  // visible text 1:1 (Google de-indexes mismatched markup).
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  // Schema.org ItemList — structured "top N alternatives" list. This is
  // the markup LLMs + Google AI-overviews ingest when answering "best
  // <competitor> alternatives". Only emitted when the listicle is present.
  const itemListSchema =
    alternatives && alternatives.length >= 2
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: t('alternatives.headline', { competitor: competitorName }),
          itemListOrder: 'https://schema.org/ItemListOrderDescending',
          numberOfItems: alternatives.length,
          itemListElement: alternatives.map((alt, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: alt.name,
            description: alt.pitch,
          })),
        }
      : null;

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {itemListSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      )}
      <BreadcrumbJsonLd
        items={[
          { name: t('breadcrumb.home'), url: '/' },
          { name: t('breadcrumb.comparison'), url: `/${pageSlug}` },
          { name: t('breadcrumb.competitorAlternative', { competitor: competitorName }), url: `/${pageSlug}` },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <Section tone="default" ariaLabelledBy={heroId}>
        <div className="mx-auto max-w-3xl px-4 pt-16 text-center sm:pt-24">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-600">
            {t('hero.eyebrow')}
          </p>
          <h1
            id={heroId}
            className="mt-3 text-4xl font-semibold leading-tight tracking-tight text-gray-950 sm:text-5xl"
          >
            {heroTitle}
          </h1>
          <div className="mt-6 text-lg leading-relaxed text-gray-600">{heroBody}</div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <CTAButton href="#vergleich" ariaLabel={t('hero.ctaPrimaryAria')}>
              {t('hero.ctaPrimary')}
            </CTAButton>
            <Link
              href="/register?source=comparison"
              onClick={() => trackLandingCtaClicked('comparison')}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-800 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              {t('hero.ctaSecondary')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs text-gray-500">
            {t('hero.trustLine')}
          </p>
        </div>
      </Section>

      {/* TL;DR / honest verdict */}
      <Section tone="muted">
        <Reveal>
          <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-8">
            <h2 className="text-xl font-semibold tracking-tight text-gray-950">
              {t('verdict.title')}
            </h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-sm font-semibold text-gray-950">
                  {t('verdict.competitorBetter', { competitor: competitorName })}
                </p>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-gray-700">
                  {verdict.competitorBetterWhen.map((item, idx) => (
                    <li key={idx} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-950">
                  {t('verdict.eazybookingBetter')}
                </p>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-gray-700">
                  {verdict.eazybookingBetterWhen.map((item, idx) => (
                    <li key={idx} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Comparison table */}
      <Section id="vergleich" tone="default" ariaLabelledBy={tableId}>
        <SectionHeader
          eyebrow={t('table.eyebrow')}
          headline={t('table.headline', { competitor: competitorName })}
          description={t('table.description')}
          headlineId={tableId}
          size="xl"
        />
        <Reveal>
          <div className="mx-auto mt-10 max-w-4xl px-4">
            {/* Tally-Stat-Block — quick at-a-glance count of comparison
                outcomes. Pure heuristic from the table data (no manual
                annotation needed). Pattern from Stripe/Linear/Vercel
                comparison pages. */}
            <div className="mx-auto mb-8 grid max-w-2xl grid-cols-3 gap-3 sm:gap-4">
              <TallyStat
                value={tally.wins}
                label={t('table.tallyWin')}
                tone="win"
              />
              <TallyStat value={tally.ties} label={t('table.tallyTie')} tone="tie" />
              <TallyStat
                value={tally.misses}
                label={t('table.tallyMiss', { competitor: competitorName })}
                tone="miss"
              />
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              {/* Table header — hidden on mobile (stacked-card layout
                  uses inline labels instead). */}
              <div className="hidden grid-cols-[1.4fr_1fr_1fr] gap-4 border-b border-gray-200 bg-gray-50/60 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:grid">
                <div>{t('table.columnFeature')}</div>
                <div className="text-blue-700">{t('table.columnEazyBooking')}</div>
                <div className="text-gray-700">{competitorName}</div>
              </div>
              <ul className="divide-y divide-gray-100">
                {visibleRows.map((row, idx) => (
                  <li key={idx} className="px-4 py-4 sm:px-6 sm:py-5">
                    {/* ≥ sm: original 3-col grid */}
                    <div className="hidden sm:grid sm:grid-cols-[1.4fr_1fr_1fr] sm:gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-950">{row.label}</p>
                        {row.note && (
                          <p className="mt-1 text-xs italic text-gray-500">{row.note}</p>
                        )}
                      </div>
                      <ComparisonCell value={row.eazybooking} yesLabel={t('table.cellYes')} noLabel={t('table.cellNo')} />
                      <ComparisonCell value={row.competitor} yesLabel={t('table.cellYes')} noLabel={t('table.cellNo')} />
                    </div>
                    {/* < sm: stacked-card layout — label on top, then
                        labelled value rows. Avoids the cramped 3-col
                        grid on narrow viewports (<640px). */}
                    <div className="sm:hidden">
                      <p className="text-sm font-semibold text-gray-950">{row.label}</p>
                      {row.note && (
                        <p className="mt-1 text-xs italic text-gray-500">{row.note}</p>
                      )}
                      <dl className="mt-3 space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-xs font-medium uppercase tracking-wide text-blue-700">
                            {t('table.columnEazyBooking')}
                          </dt>
                          <dd className="text-right">
                            <ComparisonCell value={row.eazybooking} yesLabel={t('table.cellYes')} noLabel={t('table.cellNo')} />
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            {competitorName}
                          </dt>
                          <dd className="text-right">
                            <ComparisonCell value={row.competitor} yesLabel={t('table.cellYes')} noLabel={t('table.cellNo')} />
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Collapsible toggle — F-500 pattern (Stripe pricing
                  page): show first N rows by default, expose "Show all"
                  for users who want the full picture without making
                  everyone scroll past 18+ rows. */}
              {hasMoreRows && (
                <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 text-center sm:px-6">
                  <button
                    type="button"
                    onClick={() => setShowAll((s) => !s)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50"
                    aria-expanded={showAll}
                  >
                    {showAll
                      ? t('table.showLess')
                      : t('table.showAll', { count: comparison.length })}
                    <ArrowRight
                      className={`h-3.5 w-3.5 transition-transform ${showAll ? 'rotate-[270deg]' : 'rotate-90'}`}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Best-alternatives listicle — AEO-critical. Ranked <ol> so LLMs
          and Google parse it as a genuine "top N alternatives" list, not
          a 1-vs-1 page. EazyBooking is #1 (honest why), others listed
          fairly below. Only renders when the page supplies ≥2 entries. */}
      {alternatives && alternatives.length >= 2 && (
        <Section id="alternativen" tone="default" ariaLabelledBy={altId}>
          <SectionHeader
            eyebrow={t('alternatives.eyebrow')}
            headline={t('alternatives.headline', { competitor: competitorName })}
            description={t('alternatives.intro', { competitor: competitorName })}
            headlineId={altId}
            size="xl"
          />
          <Reveal>
            <ol className="mx-auto mt-10 max-w-3xl space-y-4 px-4">
              {alternatives.map((alt, idx) => (
                <li
                  key={idx}
                  className={
                    alt.isUs
                      ? 'rounded-2xl border-2 border-blue-500 bg-blue-50/40 p-5 shadow-[0_2px_12px_rgba(37,99,235,0.10)] sm:p-6'
                      : 'rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-6'
                  }
                >
                  <div className="flex items-start gap-4">
                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold tabular-nums ${
                        alt.isUs
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-gray-950">
                          {(() => {
                            const targetSlug = alt.isUs
                              ? null
                              : resolveCompetitorSlug(alt.name);
                            // Link to our own comparison page for this vendor
                            // — but never self-link the current page.
                            if (targetSlug && targetSlug !== pageSlug) {
                              return (
                                <Link
                                  href={`/${targetSlug}`}
                                  className="group inline-flex items-center gap-1 underline decoration-gray-300 decoration-1 underline-offset-2 transition-colors hover:text-blue-700 hover:decoration-blue-400"
                                >
                                  {alt.name}
                                  <ArrowRight className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-blue-600" />
                                </Link>
                              );
                            }
                            return alt.name;
                          })()}
                        </h3>
                        {alt.isUs && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                            <Award className="h-3 w-3" aria-hidden="true" />
                            {t('alternatives.usBadge')}
                          </span>
                        )}
                        {alt.bestFor && (
                          <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
                            {alt.bestFor}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {alt.pitch}
                      </p>
                      {alt.isUs && (
                        <Link
                          href="/register?source=comparison"
                          onClick={() => trackLandingCtaClicked('comparison')}
                          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 transition-colors hover:text-blue-800"
                        >
                          {t('alternatives.ctaUs')}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>
        </Section>
      )}

      {/* Differentiator cards */}
      <Section tone="muted" ariaLabelledBy={diffId}>
        <SectionHeader
          eyebrow={t('differentiators.eyebrow')}
          headline={t('differentiators.headline')}
          headlineId={diffId}
          size="lg"
        />
        <Reveal>
          <div className="mx-auto mt-12 grid max-w-5xl gap-5 px-4 sm:grid-cols-2">
            {differentiators.map((d, idx) => (
              <Differentiator key={idx} {...d} />
            ))}
          </div>
        </Reveal>
      </Section>

      {/* Migration callout — matches the winning DE rankers' "how to switch"
          section AND links to our (indexed) step-by-step blog guide. Only
          renders for competitors we have a guide for. */}
      {migrationGuide && (
        <Section tone="default">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-start gap-5 rounded-2xl border border-blue-200/70 bg-blue-50/40 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
                  {t('migration.eyebrow')}
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-gray-950 sm:text-2xl">
                  {t('migration.headline', { competitor: competitorName })}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                  {t('migration.body', { competitor: competitorName })}
                </p>
              </div>
              <Link
                href={`/blog/${migrationGuide}`}
                onClick={() => trackLandingCtaClicked('comparison_migration')}
                className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-gray-950 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
              >
                {t('migration.cta')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </Section>
      )}

      {/* Industry deep-links — see INDUSTRY_LINKS note above. Compact chip
          row so it reads as "also available for your industry" rather than
          a nav dump; every indexed comparison page thus names all 11 LPs. */}
      <Section tone="muted">
        <Reveal>
          <div className="mx-auto max-w-3xl px-4 text-center">
            <h2 className="text-xl font-semibold tracking-tight text-gray-950 sm:text-2xl">
              {t('industryLinks.title')}
            </h2>
            <ul className="mt-6 flex flex-wrap justify-center gap-2.5">
              {INDUSTRY_LINKS.map((it) => (
                <li key={it.ns}>
                  <Link
                    href={it.href}
                    className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-blue-300 hover:text-blue-700"
                  >
                    {t(`industryLinks.items.${it.ns}`)}
                    <ArrowRight className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-blue-600" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </Section>

      {/* FAQ — Schema.org FAQPage rich-snippet eligible */}
      <Section tone="default" ariaLabelledBy={faqId}>
        <SectionHeader
          eyebrow={t('faq.eyebrow')}
          headline={t('faq.headline', { competitor: competitorName })}
          headlineId={faqId}
          size="lg"
        />
        <Reveal>
          <div className="mx-auto mt-10 max-w-3xl space-y-4 px-4">
            {faqs.map((f, idx) => (
              <details
                key={idx}
                className="group rounded-xl border border-gray-200/80 bg-white px-5 py-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] open:shadow-[0_2px_8px_rgba(15,23,42,0.06)]"
              >
                <summary className="cursor-pointer list-none text-sm font-semibold text-gray-950 marker:hidden flex items-start justify-between gap-3">
                  <span>{f.question}</span>
                  <span
                    aria-hidden="true"
                    className="mt-0.5 inline-block text-gray-400 transition-transform group-open:rotate-45"
                  >
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{f.answer}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* CTA */}
      <Section tone="muted">
        <Reveal>
          <div className="mx-auto max-w-2xl px-4 text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-gray-950 sm:text-4xl">
              {t('cta.headline')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              {t('cta.body')} <strong>{t('cta.bodyEmphasis')}</strong>{t('cta.bodySuffix')}
            </p>
            <div className="mt-8 flex justify-center">
              <CTAButton
                href="/register?source=comparison"
                ariaLabel={t('cta.buttonAria')}
                onClick={() => trackLandingCtaClicked('comparison')}
              >
                {t('cta.button')}
                <Sparkles className="ml-2 h-4 w-4" />
              </CTAButton>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {t('cta.footnote')}
            </p>
          </div>
        </Reveal>
      </Section>

      <FooterV3 />
    </div>
  );
}
